import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import {
  PaziaFormButton,
  PaziaInput,
  PaziaLoader,
  PaziaFileDropzone,
  PaziaCheckbox,
} from "components/atoms";
import { useMenuConfig, shopApi, MenuConfig, MenuItem, SubmenuItem } from "api/shop";
import { useToaster } from "utils";
import { useState, useEffect } from "react";
import { MdAdd, MdDelete, MdExpandMore, MdExpandLess } from "react-icons/md";
import { useShopImageUpload } from "../Sections/SectionForms/useShopImageUpload";
import { v4 as uuidv4 } from "uuid";

export const ShopMenuForm = () => {
  const { menuConfig, isLoading, invalidate } = useMenuConfig();
  const { showSuccess, showError } = useToaster();
  const { uploadImage } = useShopImageUpload();
  const [isSaving, setIsSaving] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: getDefaultFormValues(menuConfig),
  });
  const { handleSubmit, reset, control } = methods;

  const {
    fields: menuItems,
    append: appendItem,
    remove: removeItem,
  } = useFieldArray({
    control,
    name: "items",
  });

  useEffect(() => {
    if (menuConfig) {
      reset(getDefaultFormValues(menuConfig));
    }
  }, [menuConfig, reset]);

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      const standardLogo = data.standard.logo ? await uploadImage(data.standard.logo) : undefined;
      const stickyLogo = data.sticky.logo ? await uploadImage(data.sticky.logo) : undefined;

      const formattedData: MenuConfig = {
        standard: {
          height: data.standard.height,
          logo: standardLogo,
          backgroundColor: data.standard.backgroundColor,
          textColor: data.standard.textColor,
          submenuBgColor: data.standard.submenuBgColor,
          submenuTextColor: data.standard.submenuTextColor,
          submenuTextColorHover: data.standard.submenuTextColorHover,
        },
        sticky: {
          height: data.sticky.height,
          logo: stickyLogo,
          backgroundColor: data.sticky.backgroundColor,
          textColor: data.sticky.textColor,
          submenuBgColor: data.sticky.submenuBgColor,
          submenuTextColor: data.sticky.submenuTextColor,
          submenuTextColorHover: data.sticky.submenuTextColorHover,
        },
        items: data.items.map((item, index) => ({
          id: item.id || uuidv4(),
          label: item.label,
          href: item.href,
          external: item.external || false,
          order: index,
          submenu: item.submenu?.map((sub, subIndex) => ({
            id: sub.id || uuidv4(),
            label: sub.label,
            href: sub.href,
            order: subIndex,
          })),
        })),
      };

      await shopApi.updateMenuConfig(formattedData);
      showSuccess("Zapisano konfigurację menu");
      invalidate();
    } catch (error) {
      showError("Nie udało się zapisać konfiguracji menu");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const addMenuItem = () => {
    appendItem({
      id: uuidv4(),
      label: "",
      href: "",
      external: false,
      order: menuItems.length,
      submenu: [],
    });
  };

  const toggleExpanded = (index: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid max-w-4xl gap-6">
        {/* Standard Style Config */}
        <StyleConfigSection title="Styl standardowy" prefix="standard" />

        {/* Sticky Style Config */}
        <StyleConfigSection title="Styl po przewinięciu (sticky)" prefix="sticky" />

        {/* Menu Items */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200">Pozycje menu</h3>
            <button
              type="button"
              onClick={addMenuItem}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj pozycję
            </button>
          </div>

          {menuItems.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak pozycji menu. Kliknij "Dodaj pozycję", aby dodać.
            </p>
          ) : (
            <div className="space-y-3">
              {menuItems.map((field, index) => (
                <MenuItemField
                  key={field.id}
                  index={index}
                  control={control}
                  onRemove={() => removeItem(index)}
                  isExpanded={expandedItems.has(index)}
                  onToggleExpand={() => toggleExpanded(index)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-start">
          <PaziaFormButton disabled={isSaving} />
        </div>
      </form>
    </FormProvider>
  );
};

const StyleConfigSection = ({ title, prefix }: { title: string; prefix: "standard" | "sticky" }) => {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <h3 className="mb-4 text-lg font-medium text-slate-800 dark:text-slate-200">{title}</h3>
      <div className="grid gap-4">
        <div className="grid gap-4 md:grid-cols-2">
          <PaziaInput name={`${prefix}.height`} label="Wysokość (px)" type="number" required />
          <div className="[&_img]:max-h-16">
            <PaziaFileDropzone name={`${prefix}.logo`} label="Logo" type="image" single preview="image" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <PaziaInput name={`${prefix}.backgroundColor`} label="Kolor tła" placeholder="#ffffff" />
          <PaziaInput name={`${prefix}.textColor`} label="Kolor tekstu" placeholder="#333333" />
          <PaziaInput name={`${prefix}.submenuBgColor`} label="Kolor tła submenu" placeholder="#ffffff" />
          <PaziaInput name={`${prefix}.submenuTextColor`} label="Kolor tekstu submenu" placeholder="#333333" />
          <PaziaInput name={`${prefix}.submenuTextColorHover`} label="Kolor tekstu submenu (hover)" placeholder="#3b82f6" />
        </div>
      </div>
    </div>
  );
};

interface MenuItemFieldProps {
  index: number;
  control: any;
  onRemove: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const MenuItemField = ({ index, control, onRemove, isExpanded, onToggleExpand }: MenuItemFieldProps) => {
  const {
    fields: submenuItems,
    append: appendSubmenu,
    remove: removeSubmenu,
  } = useFieldArray({
    control,
    name: `items.${index}.submenu`,
  });

  const addSubmenuItem = () => {
    appendSubmenu({
      id: uuidv4(),
      label: "",
      href: "",
      order: submenuItems.length,
    });
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-700">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Pozycja #{index + 1}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onToggleExpand}
            className="rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-200 dark:hover:bg-slate-600"
            title={isExpanded ? "Zwiń submenu" : "Rozwiń submenu"}
          >
            {isExpanded ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
            title="Usuń pozycję"
          >
            <MdDelete size={18} />
          </button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <PaziaInput name={`items.${index}.label`} label="Etykieta" placeholder="Nazwa pozycji" required />
        <PaziaInput name={`items.${index}.href`} label="Link" placeholder="/strona lub https://..." required />
        <div className="flex items-end pb-1">
          <PaziaCheckbox name={`items.${index}.external`} label="Link zewnętrzny" />
        </div>
      </div>

      {/* Submenu Section */}
      {isExpanded && (
        <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
              Submenu ({submenuItems.length})
            </span>
            <button
              type="button"
              onClick={addSubmenuItem}
              className="flex items-center gap-1 rounded bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={14} />
              Dodaj
            </button>
          </div>

          {submenuItems.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">Brak pozycji submenu.</p>
          ) : (
            <div className="space-y-2">
              {submenuItems.map((subField, subIndex) => (
                <div
                  key={subField.id}
                  className="flex items-end gap-2 rounded border border-slate-200 bg-white p-2 dark:border-slate-500 dark:bg-slate-600"
                >
                  <div className="flex-1">
                    <PaziaInput
                      name={`items.${index}.submenu.${subIndex}.label`}
                      label="Etykieta"
                      placeholder="Nazwa"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <PaziaInput
                      name={`items.${index}.submenu.${subIndex}.href`}
                      label="Link"
                      placeholder="/strona"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSubmenu(subIndex)}
                    className="mb-1 rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    title="Usuń"
                  >
                    <MdDelete size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const getDefaultFormValues = (menuConfig?: MenuConfig): FormValues => ({
  standard: {
    height: menuConfig?.standard?.height ?? 90,
    logo: menuConfig?.standard?.logo ?? "",
    backgroundColor: menuConfig?.standard?.backgroundColor ?? "transparent",
    textColor: menuConfig?.standard?.textColor ?? "#ffffff",
    submenuBgColor: menuConfig?.standard?.submenuBgColor ?? "#ffffff",
    submenuTextColor: menuConfig?.standard?.submenuTextColor ?? "#333333",
    submenuTextColorHover: menuConfig?.standard?.submenuTextColorHover ?? "#3b82f6",
  },
  sticky: {
    height: menuConfig?.sticky?.height ?? 64,
    logo: menuConfig?.sticky?.logo ?? "",
    backgroundColor: menuConfig?.sticky?.backgroundColor ?? "#ffffff",
    textColor: menuConfig?.sticky?.textColor ?? "#333333",
    submenuBgColor: menuConfig?.sticky?.submenuBgColor ?? "#ffffff",
    submenuTextColor: menuConfig?.sticky?.submenuTextColor ?? "#333333",
    submenuTextColorHover: menuConfig?.sticky?.submenuTextColorHover ?? "#3b82f6",
  },
  items:
    menuConfig?.items?.map((item, index) => ({
      id: item.id,
      label: item.label,
      href: item.href,
      external: item.external ?? false,
      order: item.order ?? index,
      submenu:
        item.submenu?.map((sub, subIndex) => ({
          id: sub.id,
          label: sub.label,
          href: sub.href,
          order: sub.order ?? subIndex,
        })) ?? [],
    })) ?? [],
});

const styleConfigSchema = yup.object({
  height: yup.number().required("Wysokość jest wymagana").min(1),
  logo: yup.mixed(),
  backgroundColor: yup.string().default("transparent"),
  textColor: yup.string().default("#ffffff"),
  submenuBgColor: yup.string().default("#ffffff"),
  submenuTextColor: yup.string().default("#333333"),
  submenuTextColorHover: yup.string().default("#3b82f6"),
});

const submenuItemSchema = yup.object({
  id: yup.string(),
  label: yup.string().required("Etykieta jest wymagana"),
  href: yup.string().required("Link jest wymagany"),
  order: yup.number(),
});

const menuItemSchema = yup.object({
  id: yup.string(),
  label: yup.string().required("Etykieta jest wymagana"),
  href: yup.string().required("Link jest wymagany"),
  external: yup.boolean().default(false),
  order: yup.number(),
  submenu: yup.array().of(submenuItemSchema),
});

const schema = yup
  .object({
    standard: styleConfigSchema,
    sticky: styleConfigSchema,
    items: yup.array().of(menuItemSchema),
  })
  .required();

interface FormStyleConfig {
  height: number;
  logo: string | File;
  backgroundColor: string;
  textColor: string;
  submenuBgColor: string;
  submenuTextColor: string;
  submenuTextColorHover: string;
}

interface FormSubmenuItem {
  id?: string;
  label: string;
  href: string;
  order?: number;
}

interface FormMenuItem {
  id?: string;
  label: string;
  href: string;
  external?: boolean;
  order?: number;
  submenu?: FormSubmenuItem[];
}

interface FormValues {
  standard: FormStyleConfig;
  sticky: FormStyleConfig;
  items: FormMenuItem[];
}
